// Autometrics dashboard v3 - shared UI building blocks.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { COLORS, OTD_TARGET } from './amConfig';
import { groupStats } from './amMetrics';

// ---- formatting (Indian grouping, units always explicit) ----

export const fmtInt = (n) => (n === null || n === undefined ? '–' : Math.round(n).toLocaleString('en-IN'));
export const fmtNum = (n, d = 1) => (n === null || n === undefined ? '–'
  : n.toLocaleString('en-IN', { minimumFractionDigits: d, maximumFractionDigits: d }));
export const fmtPct = (n, d = 1) => (n === null || n === undefined ? 'Data unavailable' : `${n.toFixed(d)}%`);
export const ragColor = (p, target = OTD_TARGET) => (p === null || p === undefined ? COLORS.grey : p >= target ? COLORS.green : COLORS.red);

// ---- layout ----

export function Panel({ title, subtitle, actions, children, className = '', id }) {
  return (
    <section className={`am-panel ${className}`} id={id}>
      {(title || actions) && (
        <div className="am-panel-head">
          <div>
            {title && <h3>{title}</h3>}
            {subtitle && <p>{subtitle}</p>}
          </div>
          {actions && <div className="am-panel-actions">{actions}</div>}
        </div>
      )}
      <div className="am-panel-body">{children}</div>
    </section>
  );
}

export function Seg({ options, value, onChange, size }) {
  return (
    <div className={`am-seg ${size === 'sm' ? 'am-seg-sm' : ''}`} role="tablist">
      {options.map((o) => {
        const v = typeof o === 'string' ? o : o.value;
        const l = typeof o === 'string' ? o : o.label;
        return (
          <button key={v} type="button" role="tab" aria-selected={value === v}
            className={value === v ? 'on' : ''} onClick={() => onChange(v)}>{l}</button>
        );
      })}
    </div>
  );
}

/**
 * KPI card. delta = { text, dir: 'up'|'down'|'flat', tone: 'good'|'bad'|'neutral' }.
 */
export function KpiCard({ label, value, unit, sub, delta, accent, onClick, active, hint = 'Click to filter the dashboard' }) {
  return (
    <div className={`am-kpi ${onClick ? 'clickable' : ''} ${active ? 'active' : ''}`}
      style={accent ? { '--accent': accent } : undefined} title={onClick ? hint : undefined}
      onClick={onClick} role={onClick ? 'button' : undefined} tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}>
      <div className="am-kpi-label">{label}{unit && <span className="am-unit"> ({unit})</span>}
        {onClick && <span className="am-kpi-go" aria-hidden="true">{active ? '✕' : '↗'}</span>}
      </div>
      <div className="am-kpi-value" style={accent ? { color: accent } : undefined}>{value}</div>
      <div className="am-kpi-foot">
        {delta && (
          <span className={`am-delta ${delta.tone}`}>
            {delta.dir === 'up' ? '▲' : delta.dir === 'down' ? '▼' : '■'} {delta.text}
          </span>
        )}
        {sub && <span className="am-kpi-sub">{sub}</span>}
      </div>
    </div>
  );
}

/** Delta for a KPI card. higherIsBetter: true/false, or null for neutral volume metrics. */
export function makeDelta(cur, prev, { higherIsBetter = null, unit = '', decimals = 1, isPct = false, label } = {}) {
  if (cur === null || prev === null || cur === undefined || prev === undefined) return null;
  const diff = cur - prev;
  const dir = Math.abs(diff) < 1e-9 ? 'flat' : diff > 0 ? 'up' : 'down';
  let tone = 'neutral';
  if (higherIsBetter !== null && dir !== 'flat') tone = (diff > 0) === higherIsBetter ? 'good' : 'bad';
  // Volume cards (no better/worse direction): a fall shows red, a rise green.
  else if (dir !== 'flat') tone = diff < 0 ? 'bad' : 'good';
  let text;
  if (isPct) text = `${diff > 0 ? '+' : ''}${diff.toFixed(decimals)} pts`;
  else if (unit) text = `${diff > 0 ? '+' : ''}${fmtNum(diff, decimals)} ${unit}`;
  else text = `${diff > 0 ? '+' : ''}${prev ? ((diff / prev) * 100).toFixed(1) : '–'}%`;
  return { text: `${text}${label ? ` ${label}` : ''}`, dir, tone };
}

// ---- share bars (instead of pie charts) ----

export function ShareBar({ title, items, total, onSelect, selected = [], palette = COLORS.steel }) {
  const sum = total ?? items.reduce((a, i) => a + i.count, 0);
  return (
    <div className="am-share">
      {title && <div className="am-share-title">{title}</div>}
      <div className="am-share-track">
        {items.map((it, i) => {
          const p = sum ? (it.count / sum) * 100 : 0;
          if (p <= 0) return null;
          return (
            <div key={it.name} className={`am-share-seg ${selected.includes(it.name) ? 'sel' : ''} ${selected.length && !selected.includes(it.name) ? 'dim' : ''}`}
              style={{ width: `${p}%`, background: it.color || palette[i % palette.length] }}
              title={`${it.label || it.name}: ${fmtInt(it.count)} (${p.toFixed(1)}%)`}
              onClick={onSelect ? () => onSelect(it.name) : undefined}>
              {p >= 9 && <span>{p.toFixed(0)}%</span>}
            </div>
          );
        })}
      </div>
      <div className="am-share-legend">
        {items.map((it, i) => (
          <button key={it.name} type="button" className={selected.includes(it.name) ? 'sel' : ''}
            onClick={onSelect ? () => onSelect(it.name) : undefined}>
            <i style={{ background: it.color || palette[i % palette.length] }} />
            {it.label || it.name}
            <b>{fmtInt(it.count)}</b>
            <em>{sum ? ((it.count / sum) * 100).toFixed(1) : '0.0'}%</em>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Ranked horizontal bars (HTML, not Highcharts), optionally stacked.
 * items: [{ name, label?, total, segments: [{ key, count }], color? }]
 * series: [{ key, label, color }] - segment legend / colours.
 */
export function RankedBars({ items, series, onSelect, selected = [], valueLabel = 'shipments', colorOf }) {
  const max = Math.max(1, ...items.map((i) => i.total));
  if (!items.length) return <div className="am-empty">No data under the current filters.</div>;
  return (
    <div className="am-ranked">
      {series && series.length > 1 && (
        <div className="am-ranked-legend">
          {series.map((s) => <span key={s.key}><i style={{ background: s.color }} />{s.label}</span>)}
        </div>
      )}
      {items.map((it, idx) => {
        const tip = [`${it.label || it.name}: ${fmtInt(it.total)} ${valueLabel}`,
          ...(it.segments || []).filter((g) => g.count).map((g) => `${(series.find((s) => s.key === g.key) || {}).label || g.key}: ${fmtInt(g.count)} (${((g.count / it.total) * 100).toFixed(1)}%)`),
          ...(it.extra || [])].join('\n');
        return (
          <button type="button" key={it.name} title={tip}
            className={`am-ranked-row ${selected.includes(it.name) ? 'sel' : ''} ${selected.length && !selected.includes(it.name) ? 'dim' : ''}`}
            onClick={onSelect ? () => onSelect(it.name) : undefined}>
            <span className="am-ranked-rank">{idx + 1}</span>
            <span className="am-ranked-name">{it.label || it.name}</span>
            <span className="am-ranked-track">
              <span className="am-ranked-bar" style={{ width: `${(it.total / max) * 100}%` }}>
                {(it.segments || [{ key: 'v', count: it.total }]).map((g) => (g.count > 0 ? (
                  <span key={g.key} className="am-ranked-seg"
                    style={{ flexGrow: g.count, background: colorOf ? colorOf(it, g) : ((series || []).find((s) => s.key === g.key) || {}).color || COLORS.steel[2] }} />
                ) : null))}
              </span>
            </span>
            <span className="am-ranked-val">{fmtInt(it.total)}{it.note && <em>{it.note}</em>}</span>
          </button>
        );
      })}
    </div>
  );
}

// ---- charts ----

Highcharts.setOptions({ lang: { thousandsSep: ',', numericSymbols: null } });

const BASE = {
  chart: { backgroundColor: 'transparent', style: { fontFamily: 'inherit' }, spacing: [8, 4, 4, 4] },
  title: { text: null },
  credits: { enabled: false },
  exporting: { enabled: false },
  legend: { itemStyle: { fontWeight: '500', color: '#3b4a63', fontSize: '11.5px' }, symbolRadius: 3 },
  xAxis: { lineColor: '#d6deea', tickColor: '#d6deea', labels: { style: { color: '#55637a', fontSize: '11px' } } },
  yAxis: { gridLineColor: '#edf1f6', title: { style: { color: '#55637a', fontSize: '11px' } }, labels: { style: { color: '#55637a', fontSize: '11px' } } },
  tooltip: { shared: true, borderRadius: 8, borderColor: '#d6deea', backgroundColor: '#fff', shadow: true, style: { fontSize: '12px' } },
  plotOptions: { series: { animation: { duration: 350 }, cursor: 'pointer' }, column: { borderRadius: 2, borderWidth: 0 }, bar: { borderRadius: 2, borderWidth: 0 } },
};

function merge(a, b) {
  if (Array.isArray(b) || typeof b !== 'object' || b === null) return b;
  const out = { ...a };
  Object.keys(b).forEach((k) => { out[k] = k in (a || {}) && !Array.isArray(b[k]) && typeof b[k] === 'object' && b[k] !== null ? merge(a[k], b[k]) : b[k]; });
  return out;
}

export function Chart({ options, height = 300 }) {
  const opts = useMemo(() => merge(BASE, { ...options, chart: { height, ...(options.chart || {}) } }), [options, height]);
  return <HighchartsReact highcharts={Highcharts} options={opts} />;
}

/** Highcharts point-click -> toggle a dashboard filter. */
export const clickTo = (fn) => ({ point: { events: { click() { fn(this.category ?? this.name, this); } } } });

// ---- tables ----

/**
 * columns: [{ key, label, align, render(row), value(row) for sort, bar: true, width }]
 */
export function SortTable({ columns, rows, defaultSort, onRowClick, isSelected, maxRows, footer, emptyText = 'No shipments match the current filters.' }) {
  const [sort, setSort] = useState(defaultSort || { key: columns[1] && columns[1].key, dir: 'desc' });
  const [showAll, setShowAll] = useState(false);
  const colByKey = Object.fromEntries(columns.map((c) => [c.key, c]));
  const sorted = useMemo(() => {
    const c = colByKey[sort.key];
    if (!c) return rows;
    const val = c.value || ((r) => r[c.key]);
    return [...rows].sort((a, b) => {
      const x = val(a); const y = val(b);
      if (x === y) return 0;
      if (x === null || x === undefined) return 1;
      if (y === null || y === undefined) return -1;
      const cmp = typeof x === 'string' ? x.localeCompare(y) : x - y;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sort]); // eslint-disable-line react-hooks/exhaustive-deps
  const barMax = {};
  columns.filter((c) => c.bar).forEach((c) => { barMax[c.key] = Math.max(1, ...rows.map((r) => (c.value || ((x) => x[c.key]))(r) || 0)); });
  const shown = maxRows && !showAll ? sorted.slice(0, maxRows) : sorted;
  return (
    <div className="am-table-wrap">
      <table className="am-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`${c.align === 'right' ? 'r' : ''} ${sort.key === c.key ? 'sorted' : ''}`}
                style={c.width ? { width: c.width } : undefined}
                onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key && s.dir === 'desc' ? 'asc' : 'desc' }))}>
                {c.label}{sort.key === c.key ? (sort.dir === 'desc' ? ' ↓' : ' ↑') : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 && <tr><td colSpan={columns.length} className="am-empty">{emptyText}</td></tr>}
          {shown.map((r, i) => (
            <tr key={r.name ?? r.id ?? i} className={`${onRowClick ? 'clickable' : ''} ${isSelected && isSelected(r) ? 'sel' : ''}`}
              onClick={onRowClick ? () => onRowClick(r) : undefined}>
              {columns.map((c) => {
                const content = c.render ? c.render(r) : r[c.key];
                if (c.bar) {
                  const v = (c.value || ((x) => x[c.key]))(r) || 0;
                  return (
                    <td key={c.key} className="r am-bar-cell">
                      <span className="am-databar" style={{ width: `${(v / barMax[c.key]) * 100}%` }} />
                      <span className="am-bar-text">{content}</span>
                    </td>
                  );
                }
                return <td key={c.key} className={c.align === 'right' ? 'r' : ''}>{content}</td>;
              })}
            </tr>
          ))}
        </tbody>
        {footer && <tfoot>{footer}</tfoot>}
      </table>
      {maxRows && sorted.length > maxRows && (
        <button type="button" className="am-link" onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer' : `Show all ${fmtInt(sorted.length)} rows`}
        </button>
      )}
    </div>
  );
}

/** Standard shipment performance columns used across scorecards and drills. */
export function perfColumns(nameLabel, { exceptions = true } = {}) {
  const cols = [
    { key: 'name', label: nameLabel, render: (r) => <span className="am-name">{r.label || r.name}</span>, value: (r) => r.label || r.name },
    { key: 'total', label: 'Shipments', align: 'right', bar: true, render: (r) => fmtInt(r.total) },
    { key: 'otd', label: 'OTD %', align: 'right', render: (r) => <b style={{ color: ragColor(r.otd) }}>{fmtPct(r.otd)}</b> },
    { key: 'avgDeliveryDays', label: 'Avg delivery (days)', align: 'right', render: (r) => fmtNum(r.avgDeliveryDays) },
    { key: 'avgBreachDays', label: 'Avg breach (days)', align: 'right', render: (r) => (r.avgBreachDays === null ? '–' : <span className={r.avgBreachDays >= 5 ? 'am-breach-hot' : ''}>{fmtNum(r.avgBreachDays)}</span>) },
    { key: 'delayed', label: 'Delayed', align: 'right', render: (r) => fmtInt(r.delayed) },
    { key: 'delayPct', label: 'Delay %', align: 'right', render: (r) => fmtPct(r.delayPct) },
  ];
  if (exceptions) cols.push({ key: 'excused', label: 'Exceptions', align: 'right', render: (r) => fmtInt(r.excused) });
  return cols;
}

/**
 * Drill table with breadcrumb. levels: [{ field, label, filterKey }].
 * At the last level a row click drills through (onLeaf receives the path).
 */
export function DrillTable({ rows, levels, columnsFor = perfColumns, statsFn = groupStats, sortKey = 'total', onLeaf, leafLabel = 'Open shipments', extraColumns = [] }) {
  const [path, setPath] = useState([]);
  useEffect(() => { setPath([]); }, [levels]);
  const depth = path.length;
  const level = levels[Math.min(depth, levels.length - 1)];
  const scoped = useMemo(() => rows.filter((r) => path.every((p) => r[p.field] === p.value)), [rows, path]);
  const stats = useMemo(() => statsFn(scoped, level.field), [scoped, level, statsFn]);
  const isLeaf = depth >= levels.length - 1;
  const cols = [...columnsFor(level.label), ...extraColumns];
  return (
    <div className="am-drill">
      <div className="am-crumbs">
        <button type="button" className={depth === 0 ? 'on' : ''} onClick={() => setPath([])}>All {levels[0].label.toLowerCase()}s</button>
        {path.map((p, i) => (
          <React.Fragment key={p.field}>
            <span>›</span>
            <button type="button" className={i === depth - 1 ? 'on' : ''} onClick={() => setPath(path.slice(0, i + 1))}>{p.value}</button>
          </React.Fragment>
        ))}
        <span className="am-crumb-hint">
          {isLeaf ? `Click a row to ${leafLabel.toLowerCase()}` : `Click a row to drill into ${levels[depth + 1].label.toLowerCase()}`}
        </span>
      </div>
      <SortTable key={level.field} columns={cols} rows={stats} defaultSort={{ key: sortKey, dir: 'desc' }} maxRows={12}
        onRowClick={(r) => {
          const next = [...path, { field: level.field, value: r.name, filterKey: level.filterKey }];
          if (isLeaf) { if (onLeaf) onLeaf(next); } else setPath(next);
        }} />
    </div>
  );
}

// ---- searchable multi-select with live counts ----

export function MultiSelect({ label, selected, getOptions, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const options = useMemo(() => (open ? getOptions() : []), [open, getOptions]);
  const shown = options.filter((o) => String(o.label).toLowerCase().includes(q.toLowerCase()));
  const toggle = (v) => onChange(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  return (
    <div className={`am-ms ${selected.length ? 'has' : ''}`} ref={ref}>
      <button type="button" className="am-ms-btn" onClick={() => setOpen((o) => !o)} aria-expanded={open} title={label}>
        <span className="am-ms-label">{label}</span>
        <span className="am-ms-val">{selected.length === 0 ? 'All' : selected.length === 1 ? String(options.find((o) => o.value === selected[0])?.label ?? selected[0]) : `${selected.length} selected`}</span>
        <span className="am-ms-caret">▾</span>
      </button>
      {open && (
        <div className="am-ms-pop">
          <input autoFocus placeholder={`Search ${label.toLowerCase()}`} value={q} onChange={(e) => setQ(e.target.value)} />
          <div className="am-ms-actions">
            <button type="button" onClick={() => onChange(shown.map((o) => o.value))}>Select shown</button>
            <button type="button" onClick={() => onChange([])}>Clear</button>
          </div>
          <div className="am-ms-list">
            {shown.length === 0 && <div className="am-empty">No values</div>}
            {shown.map((o) => (
              <label key={String(o.value)} className={o.count === 0 ? 'zero' : ''}>
                <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
                <span>{o.label}</span>
                <em>{fmtInt(o.count)}</em>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
